// AI Tutor streaming presentation (doc 27, ST-01) -- the reveal engine.
//
// Network reception and presentation are separate concerns. The store holds
// the authoritative reply text the moment it arrives; this engine only decides
// how much of it is VISIBLE right now. It never delays, reorders or changes
// the text it is given, and nothing it produces is written back to the store.
//
//   setTarget(text)   the authoritative text so far (or the whole reply)
//   visible()         the part of it to show, ready for the Markdown renderer
//
// Framework-free, with its clock and frame scheduler injected, so it runs
// under Jest 24 with neither fake timers nor a DOM.
import { REVEAL_PACING } from './revealConfig'
import { segmentReveal, countBacklog } from './revealUnits'
import { stabilizePartial } from './partialMarkdown'

export const REVEAL_MODE = Object.freeze({
  // paced, unit by unit
  ANIMATED: 'animated',
  // what has arrived is visible: reduced motion, a hidden tab, a selection in
  // the bubble, an atomic reply
  IMMEDIATE: 'immediate',
})

export const REVEAL_REGIME = Object.freeze({
  IDLE: 'idle',
  NATURAL: 'natural',
  ACCELERATED: 'accelerated',
  CAPPED: 'capped',
  FLUSH: 'flush',
  IMMEDIATE: 'immediate',
})

function normalize(text) {
  // The persisted reply is the streamed text trimmed (upstream
  // `parts.join('').trim()`); leading whitespace never decides a comparison.
  return typeof text === 'string' ? text.replace(/^\s+/, '') : ''
}

/**
 * @param {Object} options
 * @param {() => number} options.now            milliseconds, monotonic enough
 * @param {(cb: Function) => *} options.requestFrame
 * @param {(id: *) => void} options.cancelFrame
 * @param {typeof REVEAL_PACING} [options.pacing]
 * @param {'animated'|'immediate'} [options.mode]
 * @param {() => void} [options.onStart]    the first unit is about to show
 * @param {(visible: string) => void} [options.onUpdate] at most once a frame
 * @param {() => void} [options.onComplete] once: everything is visible and no
 *   more text will arrive
 */
export function createRevealEngine(options) {
  const now = options.now
  const requestFrame = options.requestFrame
  const cancelFrame = options.cancelFrame
  const pacing = options.pacing || REVEAL_PACING

  let mode =
    options.mode === REVEAL_MODE.IMMEDIATE
      ? REVEAL_MODE.IMMEDIATE
      : REVEAL_MODE.ANIMATED
  let target = ''
  let complete = false
  let units = []
  let shownEnd = 0
  let visible = ''
  let carry = 0
  let holdUntil = 0
  let startAt = 0
  let started = false
  let flushRate = 0
  let lastFrameAt = 0
  let frame = null
  let finished = false
  let destroyed = false
  let regime = REVEAL_REGIME.IDLE

  // what arrived when, for the lag of each revealed unit
  let arrivals = []
  const metrics = {
    firstTargetAt: null,
    firstVisibleAt: null,
    completeAt: null,
    visualDoneAt: null,
    maxBacklog: 0,
    maxLagMs: 0,
    catchUpEntries: 0,
    snaps: 0,
    revealedUnits: 0,
    backlogArea: 0,
    activeMs: 0,
  }

  function emitUpdate() {
    if (options.onUpdate) options.onUpdate(visible)
  }

  function render() {
    const prefix = target.slice(0, shownEnd)
    visible =
      shownEnd >= target.length
        ? target
        : stabilizePartial(prefix, target, { complete })
  }

  function setRegime(next) {
    if (next === regime) return
    const catching =
      next === REVEAL_REGIME.ACCELERATED || next === REVEAL_REGIME.CAPPED
    const wasCatching =
      regime === REVEAL_REGIME.ACCELERATED || regime === REVEAL_REGIME.CAPPED
    if (catching && !wasCatching) metrics.catchUpEntries += 1
    regime = next
  }

  function noteRevealed(upTo, at) {
    if (metrics.firstVisibleAt === null && upTo > 0) metrics.firstVisibleAt = at
    for (let i = 0; i < arrivals.length; i += 1) {
      if (arrivals[i].end >= upTo) {
        metrics.maxLagMs = Math.max(metrics.maxLagMs, at - arrivals[i].at)
        break
      }
    }
  }

  function begin() {
    if (started) return
    started = true
    if (options.onStart) options.onStart()
  }

  function finish() {
    if (finished || destroyed) return
    finished = true
    setRegime(REVEAL_REGIME.IDLE)
    metrics.visualDoneAt = now()
    if (options.onComplete) options.onComplete()
  }

  function stop() {
    if (frame !== null) {
      cancelFrame(frame)
      frame = null
    }
    lastFrameAt = 0
  }

  // Everything received is visible, now.
  function showAll() {
    const before = shownEnd
    if (target) begin()
    shownEnd = target.length
    visible = target
    carry = 0
    holdUntil = 0
    if (shownEnd !== before) {
      noteRevealed(shownEnd, now())
      emitUpdate()
    }
    if (complete) finish()
  }

  function nextUnits() {
    const waiting = []
    for (let i = 0; i < units.length; i += 1) {
      const unit = units[i]
      if (unit.end <= shownEnd) continue
      if (unit.open) break
      waiting.push(unit)
    }
    return waiting
  }

  function revealOne(unit, at) {
    shownEnd = unit.end
    metrics.revealedUnits += 1
    noteRevealed(shownEnd, at)
  }

  function step() {
    frame = null
    if (destroyed || finished) return
    const at = now()
    if (at < startAt) {
      frame = requestFrame(step)
      return
    }
    const waiting = nextUnits()
    if (!waiting.length) {
      lastFrameAt = 0
      setRegime(REVEAL_REGIME.IDLE)
      // nothing revealable: either all is shown, or the reveal is waiting in
      // front of a span that has not closed yet
      if (complete && shownEnd >= target.length) finish()
      return
    }
    if (!started) {
      // the first word is there the moment the status has gone
      begin()
      revealOne(waiting[0], at)
      render()
      emitUpdate()
      lastFrameAt = at
      frame = requestFrame(step)
      return
    }
    const dt = lastFrameAt
      ? Math.min(Math.max(at - lastFrameAt, 0), pacing.maxFrameMs)
      : 1000 / 60
    lastFrameAt = at
    metrics.maxBacklog = Math.max(metrics.maxBacklog, waiting.length)
    metrics.backlogArea += waiting.length * dt
    metrics.activeMs += dt

    if (at < holdUntil) {
      frame = requestFrame(step)
      return
    }

    let rate
    if (complete) {
      if (!flushRate) {
        flushRate = Math.max(
          Math.min(
            Math.max(
              pacing.baseUnitsPerSecond,
              waiting.length / pacing.flushSeconds
            ),
            pacing.maxUnitsPerSecond
          ),
          waiting.length / pacing.hardBoundSeconds
        )
      }
      rate = flushRate
      setRegime(REVEAL_REGIME.FLUSH)
    } else {
      rate = Math.min(
        pacing.maxUnitsPerSecond,
        Math.max(
          pacing.baseUnitsPerSecond,
          waiting.length / pacing.lagTargetSeconds
        )
      )
      if (rate >= pacing.maxUnitsPerSecond) setRegime(REVEAL_REGIME.CAPPED)
      else if (rate > pacing.baseUnitsPerSecond)
        setRegime(REVEAL_REGIME.ACCELERATED)
      else setRegime(REVEAL_REGIME.NATURAL)
    }

    carry += (rate * dt) / 1000
    let count = Math.min(waiting.length, Math.floor(carry))
    let index = 0
    let changed = false
    while (count > 0) {
      const unit = waiting[index]
      revealOne(unit, at)
      carry -= 1
      count -= 1
      index += 1
      changed = true
      if (
        unit.pause &&
        !complete &&
        rate <= pacing.baseUnitsPerSecond * pacing.pauseMaxRateFactor
      ) {
        holdUntil = at + pacing.sentencePauseMs
        carry = 0
        break
      }
    }
    if (changed) {
      render()
      emitUpdate()
    }
    if (complete && shownEnd >= target.length) {
      finish()
      return
    }
    frame = requestFrame(step)
  }

  function run() {
    if (destroyed || finished || frame !== null) return
    frame = requestFrame(step)
  }

  return {
    /**
     * @param {string} text the authoritative text: what the stream has
     *   delivered so far, or the persisted reply
     * @param {{complete?: boolean, delayMs?: number}} [opts] `delayMs` holds
     *   the first unit back (the status is fading out); it applies only before
     *   anything is visible
     */
    setTarget(text, opts) {
      if (destroyed || finished) return
      const next = normalize(text)
      const nowComplete = !!(opts && opts.complete) || complete
      const at = now()

      if (next !== target) {
        const shown = target.slice(0, shownEnd).replace(/\s+$/, '')
        if (next.startsWith(shown)) {
          shownEnd = Math.min(shownEnd, next.length)
        } else {
          // Not a continuation of what is on screen (a reply resumed after
          // text was lost, a corrected text): show it as it is rather than
          // mixing two texts.
          metrics.snaps += 1
          shownEnd = next.length
          carry = 0
          holdUntil = 0
          if (next) begin()
        }
        target = next
        arrivals.push({ end: target.length, at })
        if (metrics.firstTargetAt === null && target) metrics.firstTargetAt = at
      }
      if (nowComplete && !complete) {
        complete = true
        metrics.completeAt = at
      }
      units = segmentReveal(target, { complete })

      if (!started && !shownEnd && opts && opts.delayMs > 0 && !startAt) {
        startAt = at + opts.delayMs
      }

      if (mode === REVEAL_MODE.IMMEDIATE) {
        stop()
        showAll()
        return
      }
      if (shownEnd >= target.length) {
        const before = visible
        render()
        if (visible !== before) emitUpdate()
        if (complete) finish()
        return
      }
      if (!started && !(at < startAt)) {
        // no status to wait for: never a frame with an empty bubble
        const waiting = nextUnits()
        if (waiting.length) {
          begin()
          revealOne(waiting[0], at)
          lastFrameAt = at
        }
      }
      const before = visible
      render()
      if (visible !== before) emitUpdate()
      run()
    },

    /** @param {'animated'|'immediate'} next */
    setMode(next) {
      if (destroyed) return
      mode =
        next === REVEAL_MODE.IMMEDIATE
          ? REVEAL_MODE.IMMEDIATE
          : REVEAL_MODE.ANIMATED
      if (finished) return
      if (mode === REVEAL_MODE.IMMEDIATE) {
        stop()
        setRegime(REVEAL_REGIME.IMMEDIATE)
        showAll()
      } else if (shownEnd < target.length) {
        run()
      }
    },

    /**
     * Shows everything received and keeps showing arrivals at once: the reply
     * is no longer paced (an atomic reply, a new student action).
     */
    completeNow() {
      if (destroyed || finished) return
      mode = REVEAL_MODE.IMMEDIATE
      stop()
      setRegime(REVEAL_REGIME.IMMEDIATE)
      showAll()
    },

    /** Cancels the frame; every later call is inert. */
    destroy() {
      destroyed = true
      stop()
    },

    visible: () => visible,
    mode: () => mode,
    regime: () => regime,
    started: () => started,
    isComplete: () => complete,
    isFinished: () => finished,
    isDestroyed: () => destroyed,
    backlog: () => countBacklog(units, shownEnd),
    // Timings and counters only -- never the reply's text.
    metrics: () => ({
      ...metrics,
      averageBacklog: metrics.activeMs
        ? metrics.backlogArea / metrics.activeMs
        : 0,
      mode,
      receivedUnits: units.length,
    }),
  }
}
